import React, { ReactElement, useEffect, useState } from 'react'
import {
  StyleSheet,
  PressableProps,
  View,
  ViewStyle,
  TextStyle,
  Pressable,
  StyleProp,
  NativeSyntheticEvent,
  TargetedEvent,
  Platform,
  ViewProps,
} from 'react-native'

import { Color, Spacing, Radius } from '../../style'
import Icon, { Name } from '../icon/icon'
import { Text } from '../typography'

interface StyleConfig {
  container?: ViewStyle
  text?: TextStyle
}

type Config = {
  default?: StyleConfig
  disabled?: StyleConfig
  pressed?: StyleConfig
  hovered?: StyleConfig
  danger?: StyleConfig
}

const regularColorConfig: Config = {
  default: StyleSheet.create({
    container: {
      backgroundColor: Color.premiumBlack,
    },
    text: {
      color: Color.simplyWhite,
    },
  }),
  disabled: StyleSheet.create({
    container: {
      backgroundColor: Color.sleepingGrey,
      borderColor: Color.calmGrey,
    },
    text: {
      color: Color.calmGrey,
    },
  }),
  pressed: StyleSheet.create({
    container: {
      borderColor: Color.lightGreyBlue,
      backgroundColor: Color.lightGreyBlue,
    },
    text: {
      color: Color.premiumBlack,
    },
  }),
  danger: StyleSheet.create({
    container: {
      backgroundColor: Color.soothingPink,
      borderColor: Color.soothingPink,
    },
    text: {
      color: Color.simplyWhite,
    },
  }),
}

const lightColorConfig: Config = {
  default: StyleSheet.create({
    container: {
      backgroundColor: Color.simplyWhite,
    },
    text: {
      color: Color.premiumBlack,
    },
  }),
  disabled: StyleSheet.create({
    container: {
      backgroundColor: Color.sleepingGrey,
      borderColor: Color.calmGrey,
    },
    text: {
      color: Color.calmGrey,
    },
  }),
  pressed: StyleSheet.create({
    container: {
      borderColor: Color.lightGreyBlue,
      backgroundColor: Color.lightGreyBlue,
    },
  }),
  /*eslint-disable react-native/no-unused-styles */
  danger: StyleSheet.create({
    container: {
      borderColor: Color.soothingPink,
    },
    text: {
      color: Color.soothingPink,
    },
  }),
  /*eslint-enable react-native/no-unused-styles */
}

const ColorConfig = {
  regular: regularColorConfig,
  light: lightColorConfig,
}

const SizeConfig = {
  S: {
    width: 32,
    height: 32,
    padding: 12,
  },
  default: {
    width: 40,
    height: 40,
    padding: 20,
  },
  M: {
    width: 100,
    height: 40,
    padding: 20,
  },
  L: {
    width: 200,
    height: 40,
    padding: 20,
  },
}

const styles = StyleSheet.create({
  container: {
    boxSizing: 'border-box',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: Color.graphiteGrey,
  },
  hovered: {
    borderWidth: 2,
    borderColor: Color.graphicGrey,
  },
  active: {
    borderColor: Color.greyBlue,
    borderWidth: 2,
  },
  content: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  text: {
    lineHeight: 16,
  },
  dangerPressed: {
    backgroundColor: Color.lightPink,
  },
  dangerActive: {
    borderColor: Color.darkRed,
  },
  pressed: {
    borderColor: Color.greyBlue,
    borderWidth: 2,
  },
  skeletonContainer: {
    boxSizing: 'border-box',
    backgroundColor: Color.simplyWhite,
    borderColor: Color.calmGrey,
  },
  skeletonText: {
    width: '100%',
    minWidth: 100,
  },
})

export const Sizes = ['M', 'L', 'S'] as const
export type Size = (typeof Sizes)[number]

export interface Props extends Omit<PressableProps, 'style'> {
  title?: string | ReactElement
  light?: boolean
  left?: Name | ReactElement
  right?: Name | ReactElement
  style?: StyleProp<ViewStyle>
  contentContainerStyle?: StyleProp<ViewStyle>
  titleStyle?: StyleProp<TextStyle>
  round?: boolean
  size?: Size
  danger?: boolean
}

const Button = (props: Props) => {
  const {
    title,
    light,
    left,
    right,
    contentContainerStyle,
    disabled,
    onFocus,
    onBlur,
    round,
    size,
    danger,
    style,
    titleStyle,
    ...otherProps
  } = props
  const [active, setActive] = useState(false)

  const updateActive = (value: boolean) => (event: NativeSyntheticEvent<TargetedEvent>) => {
    if (disabled) {
      event?.target?.blur?.()
      return
    }
    setActive(value)
    value && onFocus && onFocus(event)
    !value && onBlur && onBlur(event)
  }
  useEffect(() => {
    if (disabled) setActive(false)
  }, [disabled])

  const colorConfig = ColorConfig[light ? 'light' : 'regular']

  const renderContent = (isPressed: boolean) => {
    const textStyle = [
      styles.text,
      colorConfig?.default?.text,
      isPressed && colorConfig?.pressed?.text,
      danger && colorConfig?.danger?.text,
      disabled && colorConfig?.disabled?.text,
      titleStyle,
    ]

    const leftStyle = (!!title || !!right) && { marginRight: Spacing.XS }
    const rightStyle = (!!title || !!left) && { marginLeft: Spacing.XS }

    return (
      <View style={styles.content}>
        {left &&
          (typeof left === 'string' ? (
            <Icon name={left} size={16} style={[textStyle, leftStyle]} />
          ) : (
            <View style={leftStyle}>{left}</View>
          ))}
        {!!title && <Text style={textStyle}>{title}</Text>}
        {right &&
          (typeof right === 'string' ? (
            <Icon name={right} size={16} style={[textStyle, rightStyle]} />
          ) : (
            <View style={leftStyle}>{right}</View>
          ))}
      </View>
    )
  }

  const renderButton = (state: { pressed: boolean; hovered?: boolean }) => {
    const { pressed, hovered } = state

    const borderRadius = (round ? Radius.L : Radius.S) + (!active && hovered ? 0 : 0)

    const sizeKey = size || 'default'
    const baseSize = SizeConfig[sizeKey]
    const borderExpanded = (!active && hovered) || pressed || active
    const paddingHorizontal = borderExpanded ? Math.max(baseSize.padding - 1, 0) : baseSize.padding

    const sizeConfig = {
      minWidth: baseSize.width,
      height: baseSize.height,
      paddingHorizontal,
    }

    const containerStyle = [
      styles.container,
      { borderRadius },
      colorConfig?.default?.container,
      sizeConfig,
      !active && hovered && styles.hovered,
      !active && hovered && colorConfig?.hovered?.container,
      pressed && colorConfig?.pressed?.container,
      pressed && styles.pressed,
      danger && colorConfig?.danger?.container,
      danger && pressed && styles.dangerPressed,
      active && styles.active,
      active && danger && styles.dangerActive,
      contentContainerStyle,
      !title && (!left || !right) && { paddingHorizontal: 0 },
      disabled && colorConfig.disabled?.container,
    ]

    return (
      <View style={containerStyle}>
        {renderContent(pressed)}
      </View>
    )
  }

  return (
    <Pressable
      disabled={disabled}
      onFocus={updateActive(true)}
      onBlur={updateActive(false)}
      style={[
        Platform.select({
          // @ts-expect-error outline style none is not defined
          web: {
            outlineStyle: 'none',
          } as ViewStyle,
        }),
        style,
      ]}
      {...otherProps}
    >
      {renderButton}
    </Pressable>
  )
}

interface SkeletonProps extends ViewProps {
  contentStyle?: StyleProp<ViewStyle>
  size?: Size
  round?: boolean
}

Button.Skeleton = (props: SkeletonProps) => {
  const { size, round, contentStyle, style, ...otherProps } = props

  const borderRadius = round ? Radius.L : Radius.S

  const sizeConfig = {
    minWidth: SizeConfig[size || 'default'].width,
    height: SizeConfig[size || 'default'].height,
    paddingHorizontal: SizeConfig[size || 'default'].padding,
  }

  const containerStyle = [styles.container, styles.skeletonContainer, { borderRadius }, sizeConfig, style]
  return (
    <View style={containerStyle} {...otherProps}>
      <Text.Skeleton style={[styles.skeletonText, contentStyle]} />
    </View>
  )
}
export default Button
